export type TrackingEvent =
  | { name: 'download_click'; params: { platform: string; type: 'store_badge' | 'store_card' | 'desktop' } }
  | { name: 'contact_address_click'; params: { label: string } }
  | { name: 'contact_phone_click'; params: { phone: string } }
  | { name: 'social_link_click'; params: { platform: string; url: string } }
  | { name: 'course_card_flip'; params: { courseId: string | number; title: string } }
  | { name: 'course_filter'; params: { filterType: 'search' | 'subject' | 'grade'; value: string } };

export function useAppTracking() {
  const { gtag } = useGtag();

  // Session-level tracker to deduplicate course flips
  const trackedCourses = new Set<string | number>();

  /**
   * Internal generic dispatcher for Google Analytics events.
   */
  const sendEvent = <T extends TrackingEvent>(event: T) => {
    try {
      gtag('event', event.name, event.params);
    } catch (err) {
      console.warn(`[Tracking] Failed to log event "${event.name}":`, err);
    }
  };

  const trackDownload = (platform: string, type: 'store_badge' | 'store_card' | 'desktop') => {
    sendEvent({
      name: 'download_click',
      params: { platform, type },
    });
  };

  const trackAddress = (label: string) => {
    sendEvent({
      name: 'contact_address_click',
      params: { label },
    });
  };

  const trackPhone = (phone: string) => {
    sendEvent({
      name: 'contact_phone_click',
      params: { phone },
    });
  };

  const trackSocial = (platform: string, url: string) => {
    sendEvent({
      name: 'social_link_click',
      params: { platform, url },
    });
  };

  const trackCourseFlip = (courseId: string | number, title: string) => {
    if (trackedCourses.has(courseId)) return;
    trackedCourses.add(courseId);

    sendEvent({
      name: 'course_card_flip',
      params: { courseId, title },
    });
  };

  const trackCourseFilter = (filterType: 'search' | 'subject' | 'grade', value: string) => {
    sendEvent({
      name: 'course_filter',
      params: { filterType, value },
    });
  };

  return {
    trackDownload,
    trackAddress,
    trackPhone,
    trackSocial,
    trackCourseFlip,
    trackCourseFilter,
  };
}
